import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { hero, brand } from '../content';

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 50, damping: 20 },
  },
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-bone overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-12 gap-y-12 md:gap-x-10 items-end"
        >
          {/* Eyebrow + headline */}
          <div className="col-span-12 md:col-span-7">
            <motion.div variants={item} className="flex items-center gap-3 mb-8">
              <span className="block w-10 h-px bg-taupe" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-taupe">
                {hero.eyebrow}
              </span>
            </motion.div>

            <motion.h1
              variants={item}
              data-testid="hero-headline"
              className="font-serif text-charcoal text-[44px] leading-[1.04] sm:text-6xl lg:text-[88px] tracking-[-0.02em]"
            >
              {hero.headline.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </motion.h1>

            <motion.p
              variants={item}
              data-testid="hero-subheadline"
              className="mt-8 max-w-xl text-base md:text-lg text-charcoal/70 leading-relaxed"
            >
              {hero.subheadline}
            </motion.p>

            <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-charcoal text-bone px-7 py-4 text-sm tracking-wide hover:bg-charcoal/85 transition-all duration-300"
              >
                {hero.ctaPrimary}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                href="#portfolio"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 text-charcoal px-2 py-4 text-sm tracking-wide border-b border-charcoal/40 hover:border-charcoal transition-colors duration-300"
              >
                {hero.ctaSecondary}
                <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </motion.div>
          </div>

          {/* Hero image */}
          <motion.div
            variants={item}
            className="col-span-12 md:col-span-5 relative"
            data-testid="hero-image-wrap"
          >
            <div className="relative overflow-hidden aspect-[4/5] md:aspect-[3/4] bg-taupe/10">
              <motion.img
                src={hero.image}
                alt={hero.imageAlt}
                loading="eager"
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-bone px-6 py-4">
              <p className="text-[11px] uppercase tracking-[0.3em] text-taupe">
                Currently Booking
              </p>
              <p className="font-serif text-lg text-charcoal mt-1">Spring / Summer ’26</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 md:mt-32 grid grid-cols-3 border-t border-charcoal/10"
          data-testid="hero-stats"
        >
          {hero.stats.map((s, i) => (
            <div
              key={i}
              className={`py-8 md:py-10 ${
                i !== hero.stats.length - 1 ? "border-r border-charcoal/10" : ""
              } pl-2 md:pl-6`}
            >
              <p className="font-serif text-charcoal text-3xl md:text-5xl tracking-tight">
                {s.value}
              </p>
              <p className="mt-2 text-[11px] md:text-xs uppercase tracking-[0.28em] text-taupe">
                {s.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* subtle bottom hint */}
      <div className="mt-8 max-w-[1400px] mx-auto px-6 md:px-10 flex items-center gap-3 text-taupe">
        <span className="block w-10 h-px bg-taupe/60" />
        <span className="text-[11px] uppercase tracking-[0.3em]">{brand.city}</span>
      </div>
    </section>
  );
};
